/**
 * «Hoy puedes gastar» (tarjeta «Hoy» del Inicio).
 *
 * Lo que queda en las categorías del día a día al empezar el día, repartido
 * entre los días que faltan del mes (hoy incluido). Lo gastado hoy se resta
 * de la cuota de hoy; lo que no se gaste pasa solo a los días siguientes,
 * porque mañana se reparte lo que quede entonces.
 *
 * Sin React: cifras en céntimos. `disponible` es lo que la hoja da como
 * saldo de las categorías elegidas AHORA (ya descontado lo de hoy) y
 * `gastadoHoy`, lo gastado hoy en ellas, en positivo (las devoluciones de hoy
 * lo bajan).
 */

export type EstadoHoy =
  /** Aún no se ha gastado nada hoy. */
  | 'libre'
  /** Se ha gastado algo y aún queda. */
  | 'quedan'
  /** Se ha gastado más que la cuota de hoy. */
  | 'encima'
  /** Al empezar el día ya no quedaba nada en estas categorías. */
  | 'sin';

export type Hoy = {
  estado: EstadoHoy;
  /** Lo que tocaba hoy al empezar el día. */
  cuota: number;
  /** Lo gastado hoy (≥ 0). */
  gastado: number;
  /** cuota − gastado: lo que queda hoy (negativo si se ha pasado). */
  resto: number;
  /** Lo que quedaba para el resto del mes al empezar el día. */
  alEmpezar: number;
  /** Días que faltan del mes, hoy incluido. */
  dias: number;
  /** Lo que tocará mañana si hoy no se gasta más; null el último día. */
  manana: number | null;
};

export function calcularHoy({
  disponible,
  gastadoHoy,
  dia,
  diasMes,
}: {
  disponible: number;
  gastadoHoy: number;
  /** Día del mes (1..31). */
  dia: number;
  diasMes: number;
}): Hoy {
  const gastado = Math.max(0, Math.round(gastadoHoy));
  const dias = Math.max(1, diasMes - dia + 1);
  const alEmpezar = Math.round(disponible) + gastado;
  const cuota = alEmpezar > 0 ? Math.floor(alEmpezar / dias) : 0;
  const resto = cuota - gastado;
  const quedaManana = alEmpezar - gastado;
  const manana =
    dias > 1 ? Math.max(0, Math.floor(quedaManana / (dias - 1))) : null;

  let estado: EstadoHoy;
  if (alEmpezar <= 0) {
    estado = 'sin';
  } else if (resto < 0) {
    estado = 'encima';
  } else if (gastado > 0) {
    estado = 'quedan';
  } else {
    estado = 'libre';
  }

  return { estado, cuota, gastado, resto, alEmpezar, dias, manana };
}

/** Categoría vista por la selección por defecto (lo mínimo que hace falta). */
export type CategoriaHoy = {
  id: string;
  hidden?: boolean;
  is_income?: boolean;
  /** Tipos de plantilla de objetivo de la categoría (`schedule`, `by`…). */
  plantillas?: readonly string[];
};

export type GrupoHoy = {
  id: string;
  hidden?: boolean;
  is_income?: boolean;
  categories?: readonly CategoriaHoy[];
};

/**
 * Plantillas que delatan que la categoría no es del día a día: un pago fijo
 * (`schedule`) o un ahorro con fecha o meta (`by`, `goal`).
 */
const PLANTILLAS_FUERA = new Set(['schedule', 'by', 'goal']);

/**
 * Categorías que cuentan para «Hoy» mientras la persona no elige: las del
 * grupo «Día a día» de la primera travesía si existe y tiene alguna; si
 * no, las de gasto visibles que no son pagos fijos ni ahorros y no están en
 * un grupo de gastos anuales.
 */
export function categoriasPorDefecto({
  grupos,
  grupoDia,
  gruposAnuales,
}: {
  grupos: readonly GrupoHoy[];
  grupoDia?: string | null;
  gruposAnuales?: ReadonlySet<string>;
}): string[] {
  const visibles = (g: GrupoHoy) =>
    (g.categories ?? []).filter(c => !c.hidden && !c.is_income);
  const dia = grupoDia ? grupos.find(g => g.id === grupoDia) : undefined;
  if (dia && !dia.hidden && visibles(dia).length > 0) {
    return visibles(dia).map(c => c.id);
  }
  return grupos
    .filter(g => !g.hidden && !g.is_income && !gruposAnuales?.has(g.id))
    .flatMap(visibles)
    .filter(c => !(c.plantillas ?? []).some(p => PLANTILLAS_FUERA.has(p)))
    .map(c => c.id);
}

/**
 * Pref sincronizada `hoy-categorias`: JSON con la lista de ids elegidos.
 * `null` mientras no se ha elegido (se usan las de por defecto).
 */
export function leerCategoriasHoy(
  raw: string | null | undefined,
): string[] | null {
  if (!raw) {
    return null;
  }
  try {
    const v: unknown = JSON.parse(raw);
    return Array.isArray(v)
      ? v.filter((x): x is string => typeof x === 'string')
      : null;
  } catch {
    return null;
  }
}

/** Gasto reciente, lo justo para «Repetir» y las categorías más usadas. */
export type GastoReciente = {
  date: string;
  /** Céntimos, negativo. */
  amount: number;
  category: string;
  payee: string | null;
  account: string;
};

export type Repetible = {
  category: string;
  payee: string | null;
  account: string;
  /** Céntimos, positivo. */
  importe: number;
  veces: number;
};

/**
 * «Repetir»: los gastos que más se repiten (mismo sitio, categoría e
 * importe) al menos dos veces, los más frecuentes primero y, a igualdad, el
 * más reciente. Lleva la cuenta de la última vez.
 */
export function repetibles(
  gastos: readonly GastoReciente[],
  cuantos = 3,
): Repetible[] {
  const porClave = new Map<string, Repetible & { ultima: string }>();
  for (const g of gastos) {
    if (g.amount >= 0 || !g.category) {
      continue;
    }
    const clave = `${g.payee ?? ''}|${g.category}|${g.amount}`;
    const r = porClave.get(clave);
    if (r) {
      r.veces += 1;
      if (g.date > r.ultima) {
        r.ultima = g.date;
        r.account = g.account;
      }
    } else {
      porClave.set(clave, {
        category: g.category,
        payee: g.payee,
        account: g.account,
        importe: -g.amount,
        veces: 1,
        ultima: g.date,
      });
    }
  }
  return [...porClave.values()]
    .filter(r => r.veces >= 2)
    .sort((a, b) => b.veces - a.veces || b.ultima.localeCompare(a.ultima))
    .slice(0, cuantos)
    .map(({ ultima: _ultima, ...r }) => r);
}

/**
 * Categorías para los botones de la hoja: las más usadas en los gastos
 * recientes y, si faltan, las de «Hoy» en su orden. Sin repetir ni
 * ocultas (`validas`).
 */
export function categoriasFrecuentes({
  gastos,
  deHoy,
  validas,
  cuantas,
}: {
  gastos: readonly GastoReciente[];
  deHoy: readonly string[];
  validas: ReadonlySet<string>;
  cuantas: number;
}): string[] {
  const cuenta = new Map<string, number>();
  for (const g of gastos) {
    if (g.amount < 0 && validas.has(g.category)) {
      cuenta.set(g.category, (cuenta.get(g.category) ?? 0) + 1);
    }
  }
  const usadas = [...cuenta.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([id]) => id);
  const lista: string[] = [];
  for (const id of [...usadas, ...deHoy]) {
    if (lista.length >= cuantas) {
      break;
    }
    if (validas.has(id) && !lista.includes(id)) {
      lista.push(id);
    }
  }
  return lista;
}

/** Tecla del teclado de la hoja «Apuntar gasto». */
export type TeclaImporte =
  | '0'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | ','
  | 'borrar';

/** Hasta 999.999,99 €. */
const MAX_ENTEROS = 6;

/**
 * Lo escrito tras pulsar una tecla: coma decimal (una), dos decimales como
 * mucho, sin ceros a la izquierda.
 */
export function teclear(escrito: string, tecla: TeclaImporte): string {
  if (tecla === 'borrar') {
    return escrito.slice(0, -1);
  }
  const [enteros, decimales] = escrito.split(',');
  if (tecla === ',') {
    return decimales === undefined ? `${enteros || '0'},` : escrito;
  }
  if (decimales !== undefined) {
    return decimales.length >= 2 ? escrito : escrito + tecla;
  }
  if (enteros === '0') {
    return tecla;
  }
  return enteros.length >= MAX_ENTEROS ? escrito : escrito + tecla;
}

/** Lo escrito, en céntimos (0 si no hay nada). */
export function centimosDe(escrito: string): number {
  const [enteros, decimales = ''] = escrito.split(',');
  const e = parseInt(enteros || '0', 10);
  const d = parseInt((decimales + '00').slice(0, 2), 10);
  return (Number.isFinite(e) ? e : 0) * 100 + (Number.isFinite(d) ? d : 0);
}
